import { createSignal, onCleanup, onMount, Show, For, createMemo } from "solid-js";
import { useAdminContext } from "./AdminContext";
import { useUserContext } from "./UserContext";
import socket from "./socket";
import { getDefaultEmail } from "./utils";
import { User } from "../shared/types";
import { useI18n } from "./i18n";

export default () => {
  const t = useI18n();
  const [teacherInviteLink, setTeacherInviteLink] = createSignal<string | null>(null);
  const [adminStore, setAdminStore, { editUser, deleteUser, addUser }] = useAdminContext();
  const [userStore] = useUserContext();
  const [searchTerm, setSearchTerm] = createSignal("");

  const filteredUsers = createMemo(() => {
    const term = searchTerm().toLowerCase();
    return adminStore.users.filter((user: User) =>
      user.username.toLowerCase().includes(term) ||
      user.fullname.toLowerCase().includes(term)
    );
  });

  onMount(() => {
    socket.on('teacher-invite-link', (link) => {
      setTeacherInviteLink(link);
    });
  });

  onCleanup(() => {
    socket.off('teacher-invite-link');
  });

  const generateTeacherInvite = () => {
    socket.emit('generate-teacher-invite');
  };

  return (
    <>
      <div>
        <button
          type="button"
          class="btn btn-primary btn-sm me-2"
          onClick={() => {
            const username = prompt(t("username"), "");
            if(!username) return;
            addUser({ username, role: 'student' });
          }}
        >
          {t("Add User")}
        </button>

        <button
          type="button"
          class="btn btn-success btn-sm"
          onClick={generateTeacherInvite}
        >
          {t("Generate Teacher Invite Link")}
        </button>
      </div>

      <Show when={teacherInviteLink()}>
        <div class="alert alert-info mt-3">
          <p>{t("Teacher invite link (valid for 7 days)")}:</p>
          <div class="input-group">
            <input type="text" class="form-control" value={teacherInviteLink() || ''} readonly />
            <button
              class="btn btn-outline-secondary"
              onClick={() => {
                if (teacherInviteLink()) {
                  navigator.clipboard.writeText(teacherInviteLink());
                  alert(t("Link copied to clipboard!"));
                }
              }}
            >
              {t("Copy")}
            </button>
          </div>
        </div>
      </Show>

      <div class="mt-3">
        <input
          type="text"
          class="form-control"
          placeholder={t("Search by username or fullname")}
          onInput={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <table class="table table-striped table-hover mt-3">
        <thead>
          <tr>
            <th scope="col">Id</th>
            <th scope="col">{t("Username")}</th>
            <th scope="col">{t("Full Name")}</th>
            <th scope="col">{t("Email")}</th>
            <th scope="col">{t("Role")}</th>
            <th scope="col">{t("Class")}</th>
            <th scope="col">{t("Actions")}</th>
          </tr>
        </thead>
        <tbody>
          <For each={filteredUsers()}>{(user: User, index) => {
            const isDefaultEmail = !user.email;
            const displayEmail = user.email || getDefaultEmail(user.username, user.role);

            const promoName = createMemo(() => {
              if (!user.promo_id) return null;
              return userStore.promos_by_id[user.promo_id]?.name || 'Loading...';
            });

            return (
              <tr>
                <td>{user.id}</td>
                <td>{user.username}</td>
                <td>{user.fullname}</td>
                <td>
                  <Show when={user.editing} fallback={
                    <>
                      {displayEmail}
                      {isDefaultEmail && (
                        <span class="ms-1 badge bg-secondary" title={t("Default email based on username and role")}>{t("default")}</span>
                      )}
                    </>
                  }>
                    <input
                      class="form-control"
                      type="email"
                      placeholder="user@example.com"
                      value={user.emailInput !== undefined ? user.emailInput : displayEmail}
                      onInput={(e) => setAdminStore('users', index(), { emailInput: e.target.value })}
                    />
                    {isDefaultEmail && (
                      <div class="form-text small">
                        {t("Default email - will be saved if changed")}
                      </div>
                    )}
                  </Show>
                </td>
                <td>
                  <Show when={user.editing} fallback={user.role}>
                    <select
                      class="form-select"
                      value={user.roleInput}
                      onChange={(e) => setAdminStore('users', index(), { roleInput: e.target.value })}
                    >
                      <option value="admin">{t("admin")}</option>
                      <option value="teacher">{t("teacher")}</option>
                      <option value="student">{t("student")}</option>
                    </select>
                  </Show>
                </td>
                <td>
                  {promoName() ? promoName() : <span class="text-muted">{t("No class")}</span>}
                </td>
                <td>
                  <Show when={user.editing}>
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      onClick={() => editUser(index())}
                    >
                      {t("save")}
                    </button>
                  </Show>
                  <Show when={!user.editing}>
                    <button
                      type="button"
                      class="btn btn-outline-info btn-sm"
                      onClick={() => setAdminStore('users', index(), {
                        editing: true,
                        roleInput: user.role,
                        emailInput: user.email || ""
                      })}
                    >
                      {t("edit")}
                    </button>
                  </Show>
                  {' '}
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    onClick={() => {
                      if (confirm(t('Confirm deletion'))) deleteUser(user.id);
                    }}
                  >
                    {t("delete")}
                  </button>
                </td>
              </tr>
            );
          }}</For>
        </tbody>
      </table>
    </>
  );
};
